'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

// Mirror of EditorTheme — kept here to avoid circular imports
export type AppEditorTheme =
  | 'vs-dark'
  | 'vs-light'
  | 'hc-black'
  | 'dracula'
  | 'monokai'
  | 'solarized-dark'
  | 'github-dark'
  | 'nord'
  | 'one-dark-pro'
  | 'tokyo-night'
  | 'catppuccin'
  | 'gruvbox'
  | 'ayu-dark'
  | 'material-dark'
  | 'cobalt2'
  | 'night-owl';

// Per-theme UI color palette applied to the whole app shell
const THEME_PALETTE: Record<
  AppEditorTheme,
  {
    bg: string; // main background
    surface: string; // topbar / sidebar / panel bg
    border: string; // border color
    text: string; // primary text
    muted: string; // muted / secondary text
    hover: string; // hover bg
    active: string; // active / selected bg
    isDark: boolean;
  }
> = {
  'vs-dark': {
    bg: '#1e1e1e',
    surface: '#323232',
    border: '#3c3c3c',
    text: '#d4d4d4',
    muted: '#858585',
    hover: '#3c3c3c',
    active: '#04395e',
    isDark: true,
  },
  'vs-light': {
    bg: '#ffffff',
    surface: '#f0f0f0',
    border: '#d0d0d0',
    text: '#1e1e1e',
    muted: '#6e6e6e',
    hover: '#e0e0e0',
    active: '#dbeafe',
    isDark: false,
  },
  'hc-black': {
    bg: '#000000',
    surface: '#0a0a0a',
    border: '#6fc3df',
    text: '#ffffff',
    muted: '#cccccc',
    hover: '#1a1a1a',
    active: '#1a1a1a',
    isDark: true,
  },
  dracula: {
    bg: '#282a36',
    surface: '#21222c',
    border: '#44475a',
    text: '#f8f8f2',
    muted: '#6272a4',
    hover: '#44475a',
    active: '#44475a',
    isDark: true,
  },
  monokai: {
    bg: '#272822',
    surface: '#1e1f1c',
    border: '#3e3d32',
    text: '#f8f8f2',
    muted: '#75715e',
    hover: '#3e3d32',
    active: '#49483e',
    isDark: true,
  },
  'solarized-dark': {
    bg: '#002b36',
    surface: '#073642',
    border: '#586e75',
    text: '#839496',
    muted: '#586e75',
    hover: '#073642',
    active: '#0d4a5a',
    isDark: true,
  },
  'github-dark': {
    bg: '#0d1117',
    surface: '#161b22',
    border: '#30363d',
    text: '#e6edf3',
    muted: '#8b949e',
    hover: '#21262d',
    active: '#1f6feb',
    isDark: true,
  },
  nord: {
    bg: '#2e3440',
    surface: '#3b4252',
    border: '#4c566a',
    text: '#d8dee9',
    muted: '#8fa1b3',
    hover: '#434c5e',
    active: '#4c566a',
    isDark: true,
  },
  'one-dark-pro': {
    bg: '#282c34',
    surface: '#21252b',
    border: '#3e4451',
    text: '#abb2bf',
    muted: '#5c6370',
    hover: '#2c313c',
    active: '#2c313c',
    isDark: true,
  },
  'tokyo-night': {
    bg: '#1a1b26',
    surface: '#16161e',
    border: '#292e42',
    text: '#c0caf5',
    muted: '#565f89',
    hover: '#292e42',
    active: '#364a82',
    isDark: true,
  },
  catppuccin: {
    bg: '#1e1e2e',
    surface: '#181825',
    border: '#313244',
    text: '#cdd6f4',
    muted: '#6c7086',
    hover: '#313244',
    active: '#45475a',
    isDark: true,
  },
  gruvbox: {
    bg: '#282828',
    surface: '#1d2021',
    border: '#3c3836',
    text: '#ebdbb2',
    muted: '#928374',
    hover: '#3c3836',
    active: '#504945',
    isDark: true,
  },
  'ayu-dark': {
    bg: '#0d1017',
    surface: '#0f131a',
    border: '#1a1f29',
    text: '#bfbdb6',
    muted: '#3d4354',
    hover: '#1a1f29',
    active: '#253340',
    isDark: true,
  },
  'material-dark': {
    bg: '#212121',
    surface: '#1a1a1a',
    border: '#2d2d2d',
    text: '#eeffff',
    muted: '#546e7a',
    hover: '#2d2d2d',
    active: '#1565c0',
    isDark: true,
  },
  cobalt2: {
    bg: '#193549',
    surface: '#122638',
    border: '#1f4662',
    text: '#ffffff',
    muted: '#0088ff',
    hover: '#1f4662',
    active: '#1f4662',
    isDark: true,
  },
  'night-owl': {
    bg: '#011627',
    surface: '#01111d',
    border: '#0b2942',
    text: '#d6deeb',
    muted: '#637777',
    hover: '#0b2942',
    active: '#0b2942',
    isDark: true,
  },
};

interface ThemeContextValue {
  appTheme: AppEditorTheme;
  setAppTheme: (theme: AppEditorTheme) => void;
  isDark: boolean;
  palette: (typeof THEME_PALETTE)[AppEditorTheme];
}

const ThemeContext = createContext<ThemeContextValue>({
  appTheme: 'vs-dark',
  setAppTheme: () => {},
  isDark: true,
  palette: THEME_PALETTE['vs-dark'],
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [appTheme, setAppThemeState] = useState<AppEditorTheme>('vs-dark');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('codecelest_editor_theme') as AppEditorTheme | null;
      if (saved && saved in THEME_PALETTE) {
        setAppThemeState(saved);
      }
    } catch {
      /* noop */
    }
  }, []);

  const setAppTheme = (theme: AppEditorTheme) => {
    setAppThemeState(theme);
    try {
      localStorage.setItem('codecelest_editor_theme', theme);
    } catch {
      /* noop */
    }
  };

  const palette = THEME_PALETTE[appTheme];

  // Apply CSS variables to :root so the whole app can use them
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--cc-bg', palette.bg);
    root.style.setProperty('--cc-surface', palette.surface);
    root.style.setProperty('--cc-border', palette.border);
    root.style.setProperty('--cc-text', palette.text);
    root.style.setProperty('--cc-muted', palette.muted);
    root.style.setProperty('--cc-hover', palette.hover);
    root.style.setProperty('--cc-active', palette.active);
    root.setAttribute('data-theme', palette.isDark ? 'dark' : 'light');
  }, [palette]);

  return (
    <ThemeContext.Provider data-component-id="src/contexts/ThemeContext.tsx:253:4" data-component-path="src/contexts/ThemeContext.tsx" data-component-line="253" data-component-end-line="255" data-component-file="ThemeContext.tsx" data-component-name="ThemeContext.Provider" data-component-content="%7B%22elementName%22%3A%22ThemeContext.Provider%22%7D" value={{ appTheme, setAppTheme, isDark: palette.isDark, palette }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useAppTheme() {
  return useContext(ThemeContext);
}

export { THEME_PALETTE };
